import React from 'react';
import { AlertTriangle } from 'lucide-react';
import Tooltip from '@components/common/Tooltip';
import { ScheduleItem, ScheduleItemContract } from '@app-types';
import styles from './SchedulerTableCells.module.css';

// Get open conflicts for a contract
const getOpenConflicts = (contract: ScheduleItemContract) => {
  return (contract.conflicts || []).filter((c) => c.status !== 'resolved');
};

// Helper: Get division conflicts for a contract or schedule item
const getDivisionConflicts = (
  scheduleItem: ScheduleItem,
  contract: ScheduleItemContract,
  isGroupRow: boolean,
  isChildRow: boolean
) => {
  if (isGroupRow && !isChildRow) {
    const allConflicts = scheduleItem.contracts.flatMap((c) => getOpenConflicts(c));
    return allConflicts.filter((c) => c.conflict_type_code === 'DIVISION_CONFLICT');
  }

  const openConflicts = getOpenConflicts(contract);
  return openConflicts.filter((c) => c.conflict_type_code === 'DIVISION_CONFLICT');
};

// Helper: Get division tooltip content
const getDivisionTooltipContent = (conflicts: Array<{ conflict_type_name: string }>): string => {
  if (conflicts.length === 0) return '';
  if (conflicts.length === 1) return conflicts[0].conflict_type_name;
  return `${conflicts.length} division conflicts`;
};

// Helper: Render division cell for group parent row without conflicts
const renderGroupDivisionWithoutConflict = (scheduleItem: ScheduleItem): React.ReactElement => {
  const divisions = scheduleItem.contracts.map((c) => c.division).filter((d) => d !== null && d !== undefined);
  const uniqueDivisions = Array.from(new Set(divisions));

  const divisionName = uniqueDivisions.length === 1 ? uniqueDivisions[0] : '—';

  return (
    <div className={styles.divisionCell}>
      <div className={styles.divisionName}>{divisionName}</div>
    </div>
  );
};

const DivisionCell: React.FC<{
  scheduleItem: ScheduleItem;
  contract: ScheduleItemContract;
  isGroupRow: boolean;
  isChildRow: boolean;
}> = ({ scheduleItem, contract, isGroupRow, isChildRow }) => {
  const divisionConflicts = getDivisionConflicts(scheduleItem, contract, isGroupRow, isChildRow);
  const hasDivisionConflicts = divisionConflicts.length > 0;
  const tooltipContent = getDivisionTooltipContent(divisionConflicts);

  // Parent row with conflicts - show dash + warning icon
  if (isGroupRow && !isChildRow && hasDivisionConflicts) {
    return (
      <div className={styles.divisionCell}>
        <div className={styles.divisionName}>—</div>
        <Tooltip content={tooltipContent} position="top" variant="error">
          <span className={styles.conflictWarningIcon}>
            <AlertTriangle size={16} />
          </span>
        </Tooltip>
      </div>
    );
  }

  // Parent row without conflicts - show shared division
  if (isGroupRow && !isChildRow && !hasDivisionConflicts) {
    return renderGroupDivisionWithoutConflict(scheduleItem);
  }

  // Child rows with conflicts - show individual divisions
  if (isChildRow && hasDivisionConflicts) {
    return (
      <div className={styles.divisionCell}>
        <div className={styles.divisionName}>{contract.division || 'N/A'}</div>
      </div>
    );
  }

  // Child rows without conflicts - show dash
  if (isChildRow && !hasDivisionConflicts) {
    return <span className={styles.emptyCell}>—</span>;
  }

  // Single contracts - show division + warning if needed
  return (
    <div className={styles.divisionCell}>
      <div className={styles.divisionName}>{contract.division || 'N/A'}</div>
      {hasDivisionConflicts && (
        <Tooltip content={tooltipContent} position="top" variant="error">
          <span className={styles.conflictWarningIcon}>
            <AlertTriangle size={16} />
          </span>
        </Tooltip>
      )}
    </div>
  );
};

export default DivisionCell;
